import React, { ReactNode, useContext } from 'react'
import { navigate } from 'gatsby'
import { BackToTopButton } from 'suf_storybook'

import Cookies from 'js-cookie'
import MarketoOverlay from './MarketoOverlay'
import { OverlayConstants, OverlayContext } from './OverlayContext'
import SiteHeader from './siteHeaderWrapper'
import SiteFooter from './siteFooterWrapper'
import SearchOverlay from './searchOverlay'
import AustraliaOverlay from './australianOverlay'
import useAustralianOverlay from '../hooks/useAustraliaOverlay'
import { prepareMarketoLoader } from '../lib/loadMarketoForm'

export type Props = {
  children: ReactNode
  headerInverted?: boolean
  isInsights?: boolean
  isRAC?: boolean
  slug?: string
  hasLanguageToggle?: boolean
  toggleLanguage: (selectedLang: string) => void
}
export const toggleLanguage = (languageSelected: string) => {
  const availableLanguages: any = {
    'zh-cn': '/zh-cn',
    ja: '/ja'
  }
  if (languageSelected in availableLanguages) {
    navigate(availableLanguages[languageSelected])
  } else {
    navigate('/')
  }
}

const browser = typeof window !== 'undefined' && window

if (browser) {
  window.onload = () => {
    const hash = window.location.hash.substring(1)
    setTimeout(() => {
      if (hash === 'formthankyou') {
        document.querySelector(
          '.salesforce-embedded-form .form-form'
        ).style.display = 'none'
        document.querySelector(
          '.salesforce-embedded-form .form-thankyou'
        ).style.display = 'block'
      }
      document.getElementById(hash)?.scrollIntoView()
    }, 500)
  }
}

// Helmet used here to import all the scripts from the static site
export default ({
  children,
  headerInverted = true,
  isRAC = false,
  isInsights = false,
  slug = ''
}: Props) => {
  const { activeOverlay, marketoFormId, toggleOverlay, closeOverlay } =
    useContext(OverlayContext)
  useAustralianOverlay(toggleOverlay)

  let className = ''
  if (activeOverlay === OverlayConstants.SEARCH_OVERLAY) {
    className = 'header--inverted'
  } else {
    className = headerInverted ? 'hasHeader header--inverted' : ''
  }
  if (Cookies.get('isFitchInternal') === undefined) {
    if (typeof window !== 'undefined' && window.fetch) {
      const setFitchInternalCookie = async () => {
        const req = new Request('https://fx.fitchgroup.co', {
          mode: 'no-cors'
        })
        const maxCookieExpirationDays = 400
        await fetch(req)
          .then(() =>
            Cookies.set('isFitchInternal', 'true', {
              expires: maxCookieExpirationDays
            })
          )
          .catch(() => Cookies.set('isFitchInternal', 'false'))
      }
      setFitchInternalCookie()
    }
  }

  // Prepare for potential call(s) to MktoForms2.loadForm from any
  // script, even those injected from external scripts.
  prepareMarketoLoader()

  return (
    <div className={className}>
      <div id="back-to-top">
        <SiteHeader
          hasLanguageToggle
          toggleLanguage={toggleLanguage}
          languageSlug={slug}
          toggleOverlayClick={() =>
            /* eslint-disable no-unused-expressions */
            toggleOverlay(OverlayConstants.SEARCH_OVERLAY)
          }
          isOverlayOpen={
            /* eslint-disable no-unused-expressions */
            activeOverlay === OverlayConstants.SEARCH_OVERLAY
          }
          closeOverlay={closeOverlay}
        />
        <main className="main">
          {children}
          <BackToTopButton isInsights={isInsights} isSF />
        </main>
        <SiteFooter
          handleCookiesClick={() => {
            window.evidon.notice.showConsentTool()
          }}
        />
        {activeOverlay === OverlayConstants.NONE ? null : (
          <div
            className="overlay__bg"
            onClick={() => toggleOverlay(OverlayConstants.NONE)}
            onKeyDown={() => toggleOverlay(OverlayConstants.NONE)}
            role="button"
            tabIndex={0}
          />
        )}
      </div>
      <MarketoOverlay
        handleCloseClick={() => closeOverlay()}
        isOpen={activeOverlay === OverlayConstants.MARKETO}
        isRAC={isRAC}
        marketoFormId={marketoFormId}
        slug={slug}
      />

      <SearchOverlay
        isOpen={activeOverlay === OverlayConstants.SEARCH_OVERLAY}
        handleMenuClick={() => toggleOverlay(OverlayConstants.SEARCH_OVERLAY)}
      />
      {activeOverlay === OverlayConstants.AUSTRALIA_OVERLAY && (
        <AustraliaOverlay
          handleCloseClick={() => toggleOverlay(OverlayConstants.NONE)}
          isOpen={activeOverlay === OverlayConstants.AUSTRALIA_OVERLAY}
          language="en"
        />
      )}
      {activeOverlay !== OverlayConstants.NONE ? (
        <div
          className="overlay__bg"
          onClick={() => toggleOverlay(OverlayConstants.NONE)}
          onKeyDown={() => toggleOverlay(OverlayConstants.NONE)}
          role="button"
          tabIndex={0}
        />
      ) : null}
    </div>
  )
}
